import PropTypes from 'prop-types';
import { useContext } from 'react';

import { ProjectContext } from '@core/context';
import useClassy from '@core/hooks/useClassy';
import useFeatureOptions from '@core/hooks/useFeatureOptions';
import { useProjectStore, useSuperHubStore } from '@core/store';
import { NavLinkWrapper } from '@Hub/Header/components/NavLinks';
import { categoryIcons } from '@Hub/Header/utils/categoryIconMap';
import SearchTrigger from '@Hub/Search/Trigger';
import Flex from '@ui/Flex';
import KbdShortcut from '@ui/KbdShortcut';
import RDMD from '@ui/RDMD';

import classes from './style.module.scss';

const LandingPageButton = ({ featureOptions, isPrimary = false, type }) => {
  const normalizedType = type === 'tutorials' ? 'recipes' : type;
  const text = normalizedType === 'get-started' ? 'Get Started' : featureOptions[normalizedType];
  const featureType = normalizedType === 'get-started' ? 'docs' : normalizedType;
  return (
    <NavLinkWrapper
      className={`${
        isPrimary ? classes['LandingPageHeader-button_primary'] : classes['LandingPageHeader-button_secondary']
      }`}
      item={{ text, type: featureType }}
    >
      <i className={categoryIcons[featureType]} />
      <span>{text}</span>
    </NavLinkWrapper>
  );
};

LandingPageButton.propTypes = {
  featureOptions: PropTypes.object,
  isPrimary: PropTypes.bool,
  type: PropTypes.string,
};

/**
 * Resolves landing page header data from either the SuperHub store (live-updateable)
 * or legacy ProjectContext, depending on the current environment.
 */
const useLPHeaderData = () => {
  const isSuperHub = useSuperHubStore(s => s.isSuperHub);
  const landingPromo = useProjectStore(s => s.data?.appearance?.landing_page?.promo);
  const { project } = useContext(ProjectContext);
  const {
    appearance: { promos, html_promo: htmlPromo },
  } = project;

  const title = isSuperHub ? landingPromo?.title : promos?.[0]?.title;
  const text = isSuperHub ? landingPromo?.text : promos?.[0]?.text;
  const contentType = isSuperHub ? landingPromo?.content_type : promos?.[0]?.extras?.type;
  const htmlContent = isSuperHub ? landingPromo?.html : htmlPromo;
  const buttonPrimary = isSuperHub ? landingPromo?.button_primary : promos?.[0]?.extras?.buttonPrimary;
  const buttonSecondary = isSuperHub ? landingPromo?.button_secondary : promos?.[0]?.extras?.buttonSecondary;
  const hasPrimaryButton = !!buttonPrimary && buttonPrimary !== 'none';
  const hasSecondaryButton = !!buttonSecondary && buttonSecondary !== 'none';

  return {
    buttonPrimary,
    buttonSecondary,
    contentType,
    hasPrimaryButton,
    hasSecondaryButton,
    htmlContent,
    text,
    title,
  };
};

const LandingHeader = ({ toggleSearch }) => {
  const bem = useClassy(classes, 'LandingPageHeader');
  const isSuperHub = useSuperHubStore(s => s.isSuperHub);
  const planFeatures = useProjectStore(s => s.plan.features);
  const { project } = useContext(ProjectContext);
  const { modules } = project;
  const featureOptions = useFeatureOptions(modules);
  const {
    buttonPrimary,
    buttonSecondary,
    contentType,
    hasPrimaryButton,
    hasSecondaryButton,
    htmlContent,
    text,
    title,
  } = useLPHeaderData();

  // For superhub only, hide the landing page header when it is restricted by
  // the current plan. This is b/c we're showing a pay gate banner in the
  // landing page content section and it doesn't make sense to gate both.
  if (isSuperHub && !planFeatures.landing_page) return null;

  if (contentType === 'none' && !title && !text) return <div></div>;

  return (
    <div className="container">
      {contentType === 'html' ? (
        <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
      ) : (
        <section className={bem('&', 'rm-LandingPageHeader')}>
          <h1 className={bem('-title')}>{title}</h1>
          <RDMD className={bem('-text')}>{text}</RDMD>
          {contentType === 'buttons' && (hasPrimaryButton || hasSecondaryButton) && (
            <Flex align="center" justify="start" wrap="wrap">
              {hasPrimaryButton && <LandingPageButton featureOptions={featureOptions} isPrimary type={buttonPrimary} />}
              {hasSecondaryButton && <LandingPageButton featureOptions={featureOptions} type={buttonSecondary} />}
            </Flex>
          )}
          {contentType === 'search' && (
            <SearchTrigger toggleSearch={toggleSearch}>
              <div className="icon-search1 rm-SearchToggle-icon" />
              <div className={bem('-search-placeholder', 'rm-SearchToggle-placeholder')}>Search</div>
              <KbdShortcut className="rm-SearchToggle-shortcut">⌘K</KbdShortcut>
            </SearchTrigger>
          )}
        </section>
      )}
    </div>
  );
};

LandingHeader.propTypes = {
  toggleSearch: PropTypes.func,
};

export default LandingHeader;
